// Utility to parse CHANGELOG.md into structured data

export interface ChangelogSection {
    added: string[];
    fixed: string[];
    improved: string[];
    technical: string[];
    documentation: string[];
    deprecated: string[];
    removed: string[];
    security: string[];
}

export interface ChangelogVersion {
    version: string;
    date: string;
    sections: ChangelogSection;
    rawContent: string;
}

/**
 * Parse CHANGELOG.md content into structured versions
 */
export function parseChangelog(content: string): ChangelogVersion[] {
    const versions: ChangelogVersion[] = [];

    // Split by version headers (## [version] - date)
    const versionRegex = /## \[([^\]]+)\] - (\d{4}-\d{2}-\d{2})/g;
    const matches = [...content.matchAll(versionRegex)];

    matches.forEach((match, index) => {
        const version = match[1];
        const date = match[2];
        const startIndex = match.index!;
        const endIndex = index < matches.length - 1 ? matches[index + 1].index! : content.length;
        const versionContent = content.substring(startIndex, endIndex);

        const sections: ChangelogSection = {
            added: extractSection(versionContent, '### ✨ Ajouté'),
            fixed: extractSection(versionContent, '### 🐛 Corrigé'),
            improved: extractSection(versionContent, '### 🎨 Amélioré'),
            technical: extractSection(versionContent, '### 🔧 Technique'),
            documentation: extractSection(versionContent, '### 📚 Documentation'),
            deprecated: extractSection(versionContent, '### ⚠️ Déprécié'),
            removed: extractSection(versionContent, '### 🗑️ Supprimé'),
            security: extractSection(versionContent, '### 🔒 Sécurité'),
        };

        versions.push({
            version,
            date,
            sections,
            rawContent: versionContent,
        });
    });

    return versions;
}

/**
 * Extract items from a specific section
 */
function extractSection(content: string, sectionTitle: string): string[] {
    const items: string[] = [];
    const sectionIndex = content.indexOf(sectionTitle);

    if (sectionIndex === -1) return items;

    // Find the next section or end of content
    const nextSectionIndex = content.indexOf('###', sectionIndex + sectionTitle.length);
    const sectionContent = nextSectionIndex === -1
        ? content.substring(sectionIndex)
        : content.substring(sectionIndex, nextSectionIndex);

    // Extract list items (lines starting with -)
    const lines = sectionContent.split('\n');
    lines.forEach(line => {
        const trimmed = line.trim();
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            items.push(trimmed.substring(2).trim());
        }
    });

    return items;
}

/**
 * Get icon for section type
 */
export function getSectionIcon(sectionType: keyof ChangelogSection): string {
    const icons: Record<keyof ChangelogSection, string> = {
        added: '✨',
        fixed: '🐛',
        improved: '🎨',
        technical: '🔧',
        documentation: '📚',
        deprecated: '⚠️',
        removed: '🗑️',
        security: '🔒',
    };
    return icons[sectionType] || '📝';
}

/**
 * Get color for section type (Tailwind classes)
 */
export function getSectionColor(sectionType: keyof ChangelogSection): string {
    const colors: Record<keyof ChangelogSection, string> = {
        added: 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30',
        fixed: 'text-red-400 bg-red-500/20 border-red-500/30',
        improved: 'text-blue-400 bg-blue-500/20 border-blue-500/30',
        technical: 'text-purple-400 bg-purple-500/20 border-purple-500/30',
        documentation: 'text-cyan-400 bg-cyan-500/20 border-cyan-500/30',
        deprecated: 'text-amber-400 bg-amber-500/20 border-amber-500/30',
        removed: 'text-gray-400 bg-gray-500/20 border-gray-500/30',
        security: 'text-orange-400 bg-orange-500/20 border-orange-500/30',
    };
    return colors[sectionType] || 'text-navy-400 bg-navy-500/20 border-navy-500/30';
}

/**
 * Fetch and parse CHANGELOG.md from the project root
 */
export async function fetchChangelog(): Promise<ChangelogVersion[]> {
    try {
        const response = await fetch('/CHANGELOG.md');
        if (!response.ok) {
            throw new Error('Failed to fetch CHANGELOG.md');
        }
        const content = await response.text();
        return parseChangelog(content);
    } catch (error) {
        console.error('Error fetching changelog:', error);
        return [];
    }
}
